/**
 * Product Bible Slide
 *
 * Shows a summary of the Product Bible - product information and buyer personas.
 * The full bible is used for storyboard generation and ad creative.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { BookOpen, Users, Target, CheckCircle, AlertTriangle, ChevronRight, Quote, Package, Star } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface ProductBibleSlideProps {
  artifacts: OnboardingArtifacts
}

interface Persona {
  name: string
  age?: string
  coreProblem?: string
  emotions?: string[]
  painPoints?: string[]
  angles?: string[]
  strategicImpact?: string
}

interface TopProduct {
  name: string
  category?: string
  mainBenefit: string
  targetAudience?: string
  keyFeatures?: string[]
  adAnglesUsed?: string[]
  sampleAdCopy?: string
}

interface ProductBibleData {
  brandName?: string
  topProducts?: TopProduct[]
  product?: {
    name?: string
    valueProposition?: string
    benefits?: Array<{ name: string; description: string }>
    consumerBlockers?: Array<{ category: string; blocker: string; response: string }>
    proofPoints?: string[]
    approvedClaims?: {
      canSay?: string[]
      cannotSay?: string[]
    }
  }
  personas?: Persona[]
}

export default function ProductBibleSlide({ artifacts }: ProductBibleSlideProps) {
  const [activeTab, setActiveTab] = useState<'topProducts' | 'product' | 'personas'>('topProducts')

  // Extract product bible data from artifacts
  const productBible = artifacts.productBible as ProductBibleData | null

  const brandName = productBible?.brandName || 'Your Brand'
  const topProducts = productBible?.topProducts || []
  const product = productBible?.product
  const personas = productBible?.personas || []

  const hasTopProducts = topProducts.length > 0
  const hasProductData = product?.valueProposition || (product?.benefits && product.benefits.length > 0)
  const hasPersonaData = personas.length > 0

  // If no data, show loading/generating state
  if (!hasTopProducts && !hasProductData && !hasPersonaData) {
    return (
      <div className="flex flex-col items-center text-center max-w-xl mx-auto">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6 shadow-lg"
        >
          <BookOpen className="w-8 h-8 text-white" />
        </motion.div>

        <motion.h2
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-tf-text mb-2"
        >
          Product Bible
        </motion.h2>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-tf-text-muted text-sm mb-6"
        >
          Building your product knowledge base...
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="w-full py-8"
        >
          <div className="w-32 h-1 bg-tf-bg-muted rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </div>
          <p className="text-xs text-tf-text-muted mt-4">
            Analyzing products, reviews, and building personas
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6 shadow-lg"
      >
        <BookOpen className="w-8 h-8 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-2xl font-bold text-tf-text mb-1"
      >
        Product Bible Ready
      </motion.h2>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-tf-text-muted text-sm mb-5"
      >
        Your complete creative reference for {brandName}
      </motion.p>

      {/* Tab Navigation */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-2 mb-5"
      >
        {hasTopProducts && (
          <button
            onClick={() => setActiveTab('topProducts')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === 'topProducts'
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
            )}
          >
            <Star className="w-4 h-4" />
            Top Products ({topProducts.length})
          </button>
        )}
        <button
          onClick={() => setActiveTab('product')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            activeTab === 'product'
              ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
              : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
          )}
        >
          <Target className="w-4 h-4" />
          Brand
        </button>
        <button
          onClick={() => setActiveTab('personas')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            activeTab === 'personas'
              ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
              : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
          )}
        >
          <Users className="w-4 h-4" />
          Personas ({personas.length})
        </button>
      </motion.div>

      {/* Top Products Tab Content */}
      {activeTab === 'topProducts' && hasTopProducts && (
        <motion.div
          key="topProducts"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          className="w-full space-y-3"
        >
          {topProducts.map((product, i) => (
            <motion.div
              key={product.name}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              className="p-4 rounded-xl bg-tf-bg-muted border border-tf-border text-left"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
                  <Package className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-tf-text truncate">{product.name}</h4>
                    {product.category && (
                      <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-500 border border-amber-500/20 flex-shrink-0">
                        {product.category}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-tf-text-muted mb-2 line-clamp-2">
                    {product.mainBenefit}
                  </p>
                  {product.keyFeatures && product.keyFeatures.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {product.keyFeatures.slice(0, 3).map((feature, j) => (
                        <span
                          key={j}
                          className="px-2 py-0.5 rounded text-[10px] bg-tf-bg-card border border-tf-border text-tf-text-muted"
                        >
                          {feature}
                        </span>
                      ))}
                      {product.keyFeatures.length > 3 && (
                        <span className="px-2 py-0.5 rounded text-[10px] text-tf-text-muted">
                          +{product.keyFeatures.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-xs text-tf-text-muted text-center pt-2"
          >
            Products identified from {brandName}&apos;s most advertised items
          </motion.p>
        </motion.div>
      )}

      {/* Product Tab Content */}
      {activeTab === 'product' && product && (
        <motion.div
          key="product"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          className="w-full text-left space-y-4"
        >
          {/* Value Proposition */}
          {product.valueProposition && (
            <div className="p-4 rounded-xl bg-tf-bg-muted border border-tf-border">
              <div className="flex items-center gap-2 mb-2">
                <Quote className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-medium text-tf-text-muted uppercase tracking-wide">Value Proposition</span>
              </div>
              <p className="text-sm text-tf-text">{product.valueProposition}</p>
            </div>
          )}

          {/* Benefits */}
          {product.benefits && product.benefits.length > 0 && (
            <div className="p-4 rounded-xl bg-tf-bg-muted border border-tf-border">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-tf-text-muted uppercase tracking-wide">Main Benefits</span>
              </div>
              <div className="space-y-2">
                {product.benefits.slice(0, 4).map((benefit, i) => (
                  <motion.div
                    key={benefit.name}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    className="flex items-start gap-2"
                  >
                    <ChevronRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-tf-text">{benefit.name}:</span>
                      <span className="text-sm text-tf-text-muted ml-1">{benefit.description}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Consumer Blockers Preview */}
          {product.consumerBlockers && product.consumerBlockers.length > 0 && (
            <div className="p-4 rounded-xl bg-tf-bg-muted border border-tf-border">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-medium text-tf-text-muted uppercase tracking-wide">
                  Consumer Blockers ({product.consumerBlockers.length})
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.consumerBlockers.slice(0, 4).map((blocker, i) => (
                  <motion.span
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.7 + i * 0.05 }}
                    className="px-3 py-1 rounded-full text-xs bg-amber-500/10 text-amber-500 border border-amber-500/20"
                  >
                    {blocker.category}
                  </motion.span>
                ))}
              </div>
            </div>
          )}

          {/* Proof Points Count */}
          {product.proofPoints && product.proofPoints.length > 0 && (
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex items-center justify-center gap-4 pt-2"
            >
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{product.proofPoints.length}</p>
                <p className="text-xs text-tf-text-muted">Proof Points</p>
              </div>
              {product.approvedClaims?.canSay && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-500">{product.approvedClaims.canSay.length}</p>
                  <p className="text-xs text-tf-text-muted">Approved Claims</p>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Personas Tab Content */}
      {activeTab === 'personas' && (
        <motion.div
          key="personas"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="w-full space-y-3"
        >
          {personas.length > 0 ? (
            personas.slice(0, 4).map((persona, i) => (
              <motion.div
                key={persona.name}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="p-4 rounded-xl bg-tf-bg-muted border border-tf-border text-left"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-sm font-bold">
                      {persona.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-tf-text">{persona.name}</h4>
                      {persona.age && (
                        <p className="text-xs text-tf-text-muted">{persona.age}</p>
                      )}
                    </div>
                  </div>
                  {persona.strategicImpact && (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-500">
                      {persona.strategicImpact.split(' — ')[0]}
                    </span>
                  )}
                </div>

                {persona.coreProblem && (
                  <p className="text-xs text-tf-text-muted mb-2 line-clamp-2">
                    {persona.coreProblem}
                  </p>
                )}

                {persona.angles && persona.angles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {persona.angles.slice(0, 3).map((angle, j) => (
                      <span
                        key={j}
                        className="px-2 py-0.5 rounded text-[10px] bg-tf-bg-card border border-tf-border text-tf-text-muted"
                      >
                        {angle}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))
          ) : (
            <div className="py-8 text-center">
              <Users className="w-8 h-8 text-tf-text-muted mx-auto mb-2 opacity-50" />
              <p className="text-sm text-tf-text-muted">Personas will be generated</p>
            </div>
          )}

          {personas.length > 4 && (
            <p className="text-xs text-tf-text-muted text-center pt-2">
              +{personas.length - 4} more personas in full bible
            </p>
          )}
        </motion.div>
      )}
    </div>
  )
}
